import { useCallback, useEffect, useMemo, useState } from 'react';
import { PageShell } from '../layout/page-shell.jsx';
import { useToast } from '../ui/toast.jsx';
import { usePolicyStream } from '../../hooks/use-policy-stream.js';
import { api, ApiError } from '../../api/index.js';
import { formatUtcInTimezone } from '../../lib/time.js';

const EVENT_ICONS = {
  channel: 'fa-comments',
  unit:    'fa-sitemap',
  person:  'fa-user',
  node:    'fa-network-wired',
  oss:     'fa-folder-tree',
  settings:'fa-sliders',
};

function eventIcon(eventType) {
  const prefix = (eventType || '').split('.')[0];
  return EVENT_ICONS[prefix] || 'fa-circle-dot';
}

export function PolicyPage({ currentUser }) {
  const toast = useToast();
  const tz = currentUser?.timezone || 'UTC';
  const stream = usePolicyStream({ enabled: true });

  const [versionRow, setVersionRow] = useState(null);
  const [changes, setChanges] = useState([]);
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [error, setError] = useState(null);

  const [mode, setMode] = useState('global');     // 'global' | 'node'
  const [nodeId, setNodeId] = useState('');
  const [nodes, setNodes] = useState([]);

  // Initial fetch.
  useEffect(() => {
    api.policy.version().then(setVersionRow).catch(() => {});
    api.policy.changes(50).then((r) => setChanges(r.changes || [])).catch(() => {});
    api.nodes.list().then((r) => setNodes(r.nodes || [])).catch(() => setNodes([]));
  }, []);

  // Re-fetch on every WS-announced bump.
  useEffect(() => {
    if (stream.version == null) return;
    api.policy.version().then(setVersionRow).catch(() => {});
    api.policy.changes(50).then((r) => setChanges(r.changes || [])).catch(() => {});
  }, [stream.version]);

  const loadSnapshot = useCallback(async () => {
    setSnapshotLoading(true);
    setError(null);
    try {
      const res =
        mode === 'node' && nodeId
          ? await api.policy.snapshotForNode(nodeId)
          : await api.policy.snapshot();
      setSnapshot(res.snapshot);
    } catch (err) {
      setError(err instanceof ApiError ? err.payload?.error || err.message : err?.message);
    } finally {
      setSnapshotLoading(false);
    }
  }, [mode, nodeId]);

  useEffect(() => { loadSnapshot(); }, [loadSnapshot]);

  // Refresh snapshot when version changes.
  useEffect(() => {
    if (stream.version == null) return;
    loadSnapshot();
  }, [stream.version, loadSnapshot]);

  function handleDownloadSnapshot() {
    if (!snapshot) return;
    const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tms-policy-snapshot-v${snapshot.version}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Snapshot downloaded.');
  }

  const liveVersion = stream.version ?? versionRow?.version ?? 0;
  const liveChangedAt = stream.lastChangeAt || versionRow?.lastChangeAt;
  const liveChangedBy = stream.lastChangeBy || versionRow?.lastChangeBy;
  const liveSummary = stream.lastChangeSummary || versionRow?.lastChangeSummary;

  const counts = useMemo(() => {
    if (!snapshot) return null;
    return {
      nodes: snapshot.nodes?.length || 0,
      units: snapshot.units?.length || 0,
      persons: snapshot.persons?.length || 0,
      channels: snapshot.channels?.length || 0,
    };
  }, [snapshot]);

  return (
    <PageShell
      icon="fa-broadcast-tower"
      title="Policy"
      subtitle="Canonical state distributed to mesh nodes — version, change feed, and snapshot viewer"
      actions={
        <button type="button" onClick={loadSnapshot} disabled={snapshotLoading} className="btn-secondary">
          <i className={`fa-solid fa-arrows-rotate ${snapshotLoading ? 'animate-spin' : ''}`} aria-hidden="true" />
          Refresh
        </button>
      }
    >
      <div className="space-y-6">
        {/* Hero card: version + connection status + last change */}
        <div className="card overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-line bg-gradient-to-br from-accent-soft via-white to-canvas px-6 py-5">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-accent text-white shadow-card">
                <i className="fa-solid fa-broadcast-tower text-2xl" aria-hidden="true" />
              </div>
              <div>
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-mist">
                  <span>Current policy version</span>
                  <ConnIndicator connected={stream.connected} />
                </div>
                <div className="mt-0.5 font-mono text-3xl font-semibold tracking-tight text-ink">
                  v{liveVersion}
                </div>
              </div>
            </div>
            <div className="text-right text-xs text-mist">
              {liveChangedAt ? (
                <>
                  <div>
                    Last change: <span className="text-ink">{formatUtcInTimezone(liveChangedAt, tz)}</span>
                  </div>
                  <div>
                    by <span className="font-mono text-ink">{liveChangedBy || 'system'}</span>
                  </div>
                  {liveSummary && (
                    <div className="mt-1 font-mono text-[11px]">{liveSummary}</div>
                  )}
                </>
              ) : (
                <span>No mutations recorded yet.</span>
              )}
            </div>
          </div>

          {counts && (
            <div className="grid grid-cols-2 gap-px border-t border-line bg-line sm:grid-cols-4">
              <Stat icon="fa-network-wired" label="Nodes"    value={counts.nodes} />
              <Stat icon="fa-sitemap"       label="Units"    value={counts.units} />
              <Stat icon="fa-users"         label="Persons"  value={counts.persons} />
              <Stat icon="fa-comments"      label="Channels" value={counts.channels} />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
          {/* Changes feed */}
          <div className="card overflow-hidden">
            <header className="flex items-center justify-between border-b border-line px-5 py-3">
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-clock-rotate-left text-mist" aria-hidden="true" />
                <h2 className="text-sm font-semibold text-ink">Recent changes</h2>
              </div>
              <span className="text-[11px] text-mist">last {changes.length}</span>
            </header>
            {changes.length === 0 ? (
              <div className="px-5 py-10 text-center text-sm text-mist">
                No policy changes yet.
              </div>
            ) : (
              <ul className="max-h-[480px] divide-y divide-line overflow-y-auto scrollbar-thin">
                {changes.map((c) => (
                  <li key={c.id} className="flex items-start gap-3 px-5 py-2.5 hover:bg-canvas/40">
                    <div className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-lg bg-canvas text-mist">
                      <i className={`fa-solid ${eventIcon(c.eventType)} text-[11px]`} aria-hidden="true" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[12px] text-ink">{c.eventType}</span>
                        {c.targetKind && c.targetId && (
                          <span className="font-mono text-[11px] text-mist">
                            → {c.targetKind}:{c.targetId}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-mist">
                        {formatUtcInTimezone(c.ts, tz)} · {c.actorLogin || 'system'}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Snapshot viewer */}
          <div className="card overflow-hidden">
            <header className="flex flex-col gap-3 border-b border-line px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                <i className="fa-solid fa-file-code text-mist" aria-hidden="true" />
                <h2 className="text-sm font-semibold text-ink">Snapshot</h2>
                {snapshot && (
                  <span className="rounded-full bg-canvas px-2 py-0.5 font-mono text-[10px] text-mist">
                    v{snapshot.version}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1 rounded-2xl border border-line bg-white p-1">
                  <button
                    type="button"
                    onClick={() => setMode('global')}
                    className={
                      mode === 'global'
                        ? 'rounded-xl bg-accent-soft px-3 py-1 text-xs font-semibold uppercase tracking-wider text-accent'
                        : 'rounded-xl px-3 py-1 text-xs font-semibold uppercase tracking-wider text-mist hover:text-ink'
                    }
                  >
                    Global
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode('node')}
                    className={
                      mode === 'node'
                        ? 'rounded-xl bg-accent-soft px-3 py-1 text-xs font-semibold uppercase tracking-wider text-accent'
                        : 'rounded-xl px-3 py-1 text-xs font-semibold uppercase tracking-wider text-mist hover:text-ink'
                    }
                  >
                    Per node
                  </button>
                </div>
                {mode === 'node' && (
                  <select
                    className="field max-w-[180px] text-[12px]"
                    value={nodeId}
                    onChange={(e) => setNodeId(e.target.value)}
                  >
                    <option value="">— select node —</option>
                    {nodes.map((n) => (
                      <option key={n.id} value={n.id}>
                        {n.name} · {n.tier}
                      </option>
                    ))}
                  </select>
                )}
                <button
                  type="button"
                  onClick={handleDownloadSnapshot}
                  disabled={!snapshot}
                  className="btn-secondary text-xs"
                >
                  <i className="fa-solid fa-download" aria-hidden="true" /> Download JSON
                </button>
              </div>
            </header>

            {error ? (
              <div className="border-b border-rose-200 bg-rose-soft px-5 py-3 text-sm text-rose-700">
                <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
                {error}
              </div>
            ) : null}

            {snapshotLoading && !snapshot ? (
              <div className="px-5 py-12 text-center text-mist">
                <span className="loader-spinner mx-auto mb-2 text-accent" aria-hidden="true" />
                <div className="text-sm">Loading snapshot…</div>
              </div>
            ) : !snapshot ? (
              <div className="px-5 py-10 text-center text-sm text-mist">
                {mode === 'node' && !nodeId ? 'Select a node to see its filtered snapshot.' : '—'}
              </div>
            ) : (
              <pre className="max-h-[520px] overflow-auto bg-ink/95 p-4 text-[11px] leading-snug text-white/90 scrollbar-thin">
                {JSON.stringify(snapshot, null, 2)}
              </pre>
            )}
          </div>
        </div>
      </div>
    </PageShell>
  );
}

function ConnIndicator({ connected }) {
  return (
    <span
      className={
        connected
          ? 'inline-flex items-center gap-1 rounded-full bg-success-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-success'
          : 'inline-flex items-center gap-1 rounded-full bg-warning-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-warning'
      }
    >
      <span className={connected ? 'h-1.5 w-1.5 rounded-full bg-success animate-pulse' : 'h-1.5 w-1.5 rounded-full bg-warning'} aria-hidden="true" />
      {connected ? 'Live' : 'Reconnecting'}
    </span>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="flex items-center gap-3 bg-white px-5 py-3">
      <i className={`fa-solid ${icon} text-mist`} aria-hidden="true" />
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wider text-mist">{label}</div>
        <div className="text-lg font-semibold tracking-tight text-ink">{value}</div>
      </div>
    </div>
  );
}
